/* useRadarAlertsWatcher — background hook that polls ORIO Radar
   nearby-alerts and surfaces NEW alerts as toasts + (opt-in) push
   notifications.

   Behavior:
     • Requires geolocation (uses the last cached position if fresh).
     • Polls every 10 minutes when the app is open.
     • Persists seen alert IDs in localStorage so we don't re-toast
       the same event repeatedly across sessions.
     • For 'danger' or 'warn' alerts NOT yet seen, shows a Sonner toast.
     • If browser Notification permission is granted, also fires a
       system push notification (works when the tab is in the background).
     • Non-blocking: silent on network errors.
*/
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const LS_SEEN = "orio_radar_seen_alerts_v1";
const LS_POS  = "orio_radar_last_pos_v1";
const POLL_MS = 10 * 60 * 1000;      // 10 min
const POS_MAX_AGE_MS = 60 * 60 * 1000; // 1h cached position OK
const RADIUS_KM = 40;

function getSeen() {
  try { return new Set(JSON.parse(localStorage.getItem(LS_SEEN) || "[]")); }
  catch { return new Set(); }
}
function setSeen(set) {
  // Keep last 200 ids to avoid unbounded growth.
  try { localStorage.setItem(LS_SEEN, JSON.stringify([...set].slice(-200))); }
  catch { /* private mode */ }
}

async function getPosition() {
  return new Promise((resolve) => {
    // Try cached first
    try {
      const cached = JSON.parse(localStorage.getItem(LS_POS) || "null");
      if (cached && Date.now() - cached.ts < POS_MAX_AGE_MS) {
        resolve({ lat: cached.lat, lng: cached.lng, cached: true });
        return;
      }
    } catch { /* noop */ }
    if (!navigator.geolocation) { resolve(null); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => {
        const pos = { lat: p.coords.latitude, lng: p.coords.longitude };
        try { localStorage.setItem(LS_POS, JSON.stringify({ ...pos, ts: Date.now() })); }
        catch { /* noop */ }
        resolve(pos);
      },
      () => resolve(null),
      { maximumAge: POS_MAX_AGE_MS, timeout: 10_000, enableHighAccuracy: false }
    );
  });
}

// Server-side cron pushes radar alerts when the app is closed. While the app
// is open but backgrounded, surface them via the SW (Android blocks `new Notification`).
async function firePushNotification(alert) {
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
  if (!document.hidden || !("serviceWorker" in navigator)) return;
  try {
    const reg = await navigator.serviceWorker.ready;
    await reg.showNotification(alert.title, {
      body: alert.message,
      icon: "/icons/icon-192.png",
      badge: "/icons/badge-72.png",
      tag: `radar-${alert.id}`,
      data: { url: "/app/mappa?radar=on" },
    });
  } catch { /* noop */ }
}

export default function useRadarAlertsWatcher({ enabled = true } = {}) {
  const timerRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    const tick = async () => {
      const pos = await getPosition();
      if (!pos || cancelled) return;
      try {
        const { data } = await api.get("/radar/nearby-alerts", {
          params: { lat: pos.lat, lng: pos.lng, radius_km: RADIUS_KM },
        });
        const seen = getSeen();
        const alerts = data?.alerts || [];
        for (const a of alerts) {
          if (seen.has(a.id)) continue;
          seen.add(a.id);
          // Only surface warn / danger — 'info' would spam.
          if (a.severity === "info") continue;
          // Sonner toast
          toast(a.title, {
            description: a.message,
            duration: a.severity === "danger" ? 12_000 : 8_000,
            action: {
              label: "Vedi mappa",
              onClick: () => { window.location.href = "/app/mappa?radar=on"; },
            },
            className: a.severity === "danger" ? "orio-toast-danger" : "orio-toast-warn",
          });
          firePushNotification(a);
        }
        setSeen(seen);
      } catch { /* silent */ }
    };

    // First run — small delay so the app finishes booting.
    const boot = setTimeout(tick, 8_000);
    timerRef.current = setInterval(tick, POLL_MS);

    return () => {
      cancelled = true;
      clearTimeout(boot);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [enabled]);
}

/** Convenience: request notification permission on user gesture. */
export async function requestNotifPermission() {
  if (typeof Notification === "undefined") return "unsupported";
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  try { return await Notification.requestPermission(); }
  catch { return "denied"; }
}
